// Past Client Follow-Ups queue. Agents see their own closed-client schedule;
// Admins (Team Leads) also get a Team View. Due-ness is computed on render
// from today's date (Option A: no server cron).
import { useEffect, useMemo, useState } from "react";
import { Phone, MessageSquare, Mail, Check, ChevronDown, ChevronRight, PauseCircle, X } from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  subscribeFollowUpPlans, updateTouch, rollAnniversaryForward,
  repairClientNames, reassignFollowUpPlan,
} from "../../utils/followUpPlansStore";
import { dueState, touchLabel, planIsPausedBy, householdNamesFromContacts } from "../../utils/followUpPlan";
import { followUpDraft } from "../../utils/followUpDrafts";
import { getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import TextThreadModal from "../texting/TextThreadModal";
import FollowUpEmailModal from "./FollowUpEmailModal";
import PageHeader from "../PageHeader";

const TABS = ["Due", "Upcoming", "Done", "Team View"];
const PERIODS = ["All time", "This year", "Last 90 days"];
const UPDATE_FAILED = "Could not update the check-in. Try again.";

function firstNames(clientNames) {
  return (clientNames || "")
    .split(" and ")
    .map((n) => n.trim().split(" ")[0])
    .filter(Boolean)
    .join(" and ");
}

// Flatten plans into touch rows for a tab.
function rowsFor(plans, tab, today) {
  const rows = [];
  plans.forEach((plan) => {
    Object.entries(plan.touches || {}).forEach(([touchKey, touch]) => {
      const s = dueState(touch, today);
      const want =
        tab === "Due" ? s.bucket === "due"
        : tab === "Upcoming" ? s.bucket === "upcoming" &&
          moment(touch.due).diff(moment(today), "days") <= 60
        : s.bucket === "resolved";
      if (want) rows.push({ plan, touchKey, touch, ...s });
    });
  });
  rows.sort((a, b) => (a.touch.due < b.touch.due ? -1 : 1));
  return rows;
}

// Whether a resolved touch's doneAt falls inside the Team View's selected
// period. "All time" always counts, including touches with no doneAt
// (legacy/manual data). The other periods require a real doneAt.
function inPeriod(doneAt, period) {
  if (period === "All time") return true;
  if (!doneAt) return false;
  if (period === "This year") return doneAt >= moment().startOf("year").valueOf();
  if (period === "Last 90 days") return doneAt >= Date.now() - 90 * 86400000;
  return true;
}

export default function MainFollowUps() {
  const [{ user, users, deals }] = useStateValue();
  const isAdmin = user?.role === "Admin";
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("Due");
  const [openRow, setOpenRow] = useState(null); // `${planId}:${touchKey}`
  const [contactsByPlan, setContactsByPlan] = useState({}); // dealId -> {status, contacts}
  const [textTarget, setTextTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [period, setPeriod] = useState("All time");
  const [agentFilter, setAgentFilter] = useState(null); // {id, name}
  const today = moment().format("YYYY-MM-DD");

  useEffect(() => subscribeFollowUpPlans(setPlans), []);

  const visiblePlans = useMemo(
    () =>
      plans
        .filter((p) => p.status === "active")
        .filter((p) => isAdmin || p.agentId === user?.id),
    [plans, isAdmin, user?.id],
  );

  const activePlans = useMemo(
    () => visiblePlans.filter((p) => !planIsPausedBy(p, deals)),
    [visiblePlans, deals],
  );
  const pausedPlans = useMemo(
    () => visiblePlans.filter((p) => planIsPausedBy(p, deals)),
    [visiblePlans, deals],
  );

  const rows = useMemo(
    () => (tab === "Team View" ? [] : rowsFor(activePlans, tab, today)),
    [activePlans, tab, today],
  );
  const dueCount = useMemo(
    () => rowsFor(activePlans, "Due", today).length,
    [activePlans, today],
  );

  // The Due-first-then-Upcoming queue for whichever agent is selected in
  // Team View. Upcoming only fills in when the agent has nothing due, so a
  // household with both a due and an upcoming touch doesn't double-list.
  const agentQueueRows = useMemo(() => {
    if (!agentFilter) return [];
    const plansForAgent = activePlans.filter((p) => p.agentId === agentFilter.id);
    const due = rowsFor(plansForAgent, "Due", today);
    return due.length > 0 ? due : rowsFor(plansForAgent, "Upcoming", today);
  }, [activePlans, agentFilter, today]);

  // `force` bypasses the "already have an entry" guard — the Retry link uses
  // it to re-fetch after a failure without a separate delete-then-refetch
  // round trip (which would race the stale closure of `contactsByPlan`).
  const loadContacts = async (plan, force = false) => {
    if (!force && contactsByPlan[plan.dealId]) return;
    setContactsByPlan((m) => ({ ...m, [plan.dealId]: { status: "loading", contacts: [] } }));
    try {
      // Raw sync_contacts docs come in old and new shapes; normalizeContact
      // gives every one a `.name`, primary `.phone`, and primary `.email`.
      const contacts = ((await getContactsByIds(plan.clientIds || [])) || [])
        .map(normalizeContact)
        .filter(Boolean);
      setContactsByPlan((m) => ({ ...m, [plan.dealId]: { status: "loaded", contacts } }));
      // Self-heal: a transient contact-fetch failure at closing time can
      // freeze clientNames blank forever. Now that the fetch worked, backfill
      // it — the onSnapshot subscription repaints the header once it lands.
      if (!String(plan.clientNames || "").trim()) {
        const names = householdNamesFromContacts(contacts);
        if (names) repairClientNames(plan.dealId, names).catch(console.error);
      }
    } catch {
      setContactsByPlan((m) => ({ ...m, [plan.dealId]: { status: "failed", contacts: [] } }));
    }
  };

  const agentName = (id) => {
    const u = (users || []).find((x) => x.id === id);
    return u ? `${u.firstname || ""} ${u.lastname || ""}`.trim() : "Unassigned";
  };

  // Reassign target list for the Team View bulk action: active staff only
  // (type 0), Team Leads or Agents.
  const reassignableUsers = useMemo(
    () => (users || []).filter((u) => (u.role === "Admin" || u.role === "Employee") && u.type === 0),
    [users],
  );

  // Bulk-reassigns every plan currently shown for the filtered agent (deduped
  // by dealId — a household can contribute more than one row).
  const handleReassign = async (agentId) => {
    if (!agentId) return;
    const dealIds = [...new Set(agentQueueRows.map((row) => row.plan.dealId))];
    try {
      await Promise.all(dealIds.map((id) => reassignFollowUpPlan(id, agentId)));
      toast.success("Reassigned.");
    } catch (e) {
      console.error("Follow-up plan reassign failed:", e);
      toast.error("Could not reassign. Try again.");
    }
  };

  // `failureMessage` lets callers with different stakes (a plain "mark done"
  // vs. "the email already sent, but...") show wording that matches what
  // actually happened. On failure the row is left open — closing it would
  // hide the very actions the agent needs to retry.
  const resolveTouch = async (row, patch, failureMessage = UPDATE_FAILED) => {
    try {
      await updateTouch(row.plan.dealId, row.touchKey, patch);
      if (patch.state && row.touchKey.startsWith("anniversary-")) {
        const updated = {
          ...row.plan,
          touches: {
            ...row.plan.touches,
            [row.touchKey]: { ...row.touch, ...patch },
          },
        };
        await rollAnniversaryForward(updated);
      }
      setOpenRow(null);
    } catch (e) {
      console.error("Follow-up touch update failed:", e);
      toast.error(failureMessage);
    }
  };

  // followUpDraft derives the years phrase from the touch key itself and
  // safely defaults missing fields, so the page passes only what it knows.
  const draftFor = (row) =>
    followUpDraft(row.touchKey, row.plan.side, {
      clientFirstNames: firstNames(row.plan.clientNames),
      propertyAddress: row.plan.propertyAddress,
    });

  // Roster comes from every agent with an active plan (visiblePlans), so a
  // fully-paused agent still shows up with all-zero counts instead of
  // vanishing from the table. Done/Skipped are history and count even while
  // a household is paused (visiblePlans). Due/Overdue describe what needs
  // action right now, so they only tally unpaused plans (activePlans) — the
  // same filter the queue tabs use — so a household mid-transaction can't
  // inflate an agent's Due/Overdue while showing zero rows in their queue.
  const teamCounts = useMemo(() => {
    if (!isAdmin) return [];
    const byAgent = {};
    visiblePlans.forEach((p) => {
      byAgent[p.agentId] = byAgent[p.agentId] || { due: 0, overdue: 0, done: 0, skipped: 0 };
    });
    visiblePlans.forEach((p) => {
      const rec = byAgent[p.agentId];
      Object.entries(p.touches || {}).forEach(([touchKey, t]) => {
        if (t.state === "done" && inPeriod(t.doneAt, period)) rec.done += 1;
        if (t.state === "skipped" && inPeriod(t.doneAt, period)) rec.skipped += 1;
      });
    });
    activePlans.forEach((p) => {
      const rec = byAgent[p.agentId];
      Object.entries(p.touches || {}).forEach(([touchKey, t]) => {
        const s = dueState(t, today);
        if (s.bucket === "due" && s.overdueDays > 0) rec.overdue += 1;
        else if (s.bucket === "due") rec.due += 1;
      });
    });
    return Object.entries(byAgent).map(([agentId, counts]) => ({
      agentId, name: agentName(agentId), ...counts,
    }));
  }, [visiblePlans, activePlans, isAdmin, today, users, period]);

  // Shared row renderer for the Due/Upcoming/Done tabs AND the agent-filtered
  // list under Team View — one expand/draft/actions implementation, reused
  // rather than duplicated.
  const renderQueueRow = (row) => {
    const key = `${row.plan.dealId}:${row.touchKey}`;
    const open = openRow === key;
    // Only interpolate the draft for the row that's actually expanded — long
    // lists shouldn't pay for drafts nobody's looking at.
    const draft = open ? draftFor(row) : null;
    const contactEntry = contactsByPlan[row.plan.dealId] || { status: "loading", contacts: [] };
    const contacts = contactEntry.contacts || [];
    const phoneContact = contacts.find((c) => c.phone);
    const emailContact = contacts.find((c) => c.email);
    const resolvable = row.touch.state === "pending";
    return (
      <div key={key} className="p-4">
        <button
          className="w-full text-left flex items-center justify-between gap-3"
          onClick={() => {
            setOpenRow(open ? null : key);
            loadContacts(row.plan);
          }}
        >
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[14.5px] font-medium text-slate-900">
                {row.plan.clientNames}
              </span>
              <span className="bg-blue-100 text-blue-900 text-[11px] font-medium px-2 py-0.5 rounded-full">
                {touchLabel(row.touchKey)}
              </span>
              <span className="bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5 rounded-full capitalize">
                {row.plan.side}
              </span>
              {row.overdueDays > 0 && (
                <span className="bg-red-100 text-red-800 text-[11px] font-medium px-2 py-0.5 rounded-full">
                  {row.overdueDays === 1 ? "1 day overdue" : `${row.overdueDays} days overdue`}
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-slate-500 mt-0.5">
              {row.plan.propertyAddress} · closed {moment(row.plan.closingDate).format("MM/DD/YYYY")} · Agent: {agentName(row.plan.agentId)}
            </div>
          </div>
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        </button>

        {open && (
          <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[11px] text-slate-400 mb-1">Ready draft (edit before sending)</div>
            <div className="text-[13px] text-slate-700 leading-relaxed">{draft.body}</div>

            {contactEntry.status === "loading" && (
              <div className="text-[12px] text-slate-500 mt-2">Loading contact info…</div>
            )}
            {contactEntry.status === "failed" && (
              <div className="text-[12px] text-amber-700 mt-2">
                Couldn't load contact info.{" "}
                <button
                  type="button"
                  onClick={() => loadContacts(row.plan, true)}
                  className="text-[#218FDA] underline"
                >
                  Retry
                </button>
              </div>
            )}
            {contactEntry.status === "loaded" && (
              <div className="text-[12px] text-slate-500 mt-2">
                {phoneContact?.phone ? (
                  <a href={`tel:${phoneContact.phone}`} className="text-[#218FDA]">
                    <Phone size={12} className="inline mr-1" />
                    {phoneContact.phone}
                  </a>
                ) : (
                  <span className="text-amber-700">No phone on file</span>
                )}
                {" · "}
                {emailContact?.email || <span className="text-amber-700">No email on file</span>}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => setTextTarget({ row, draft, contact: phoneContact })}
                disabled={!phoneContact}
                className="inline-flex items-center gap-1.5 border border-slate-300 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-md text-[12.5px]"
              >
                <MessageSquare size={13} /> Text draft
              </button>
              <button
                onClick={() =>
                  setEmailTarget({
                    row,
                    draft,
                    contact: emailContact,
                    // Household convention: co-clients ride along as bcc
                    // (comma-joined string; the send endpoint interpolates
                    // it into the Bcc header directly). Excludes whichever
                    // contact is the To: recipient.
                    bcc: contacts
                      .filter((c) => c !== emailContact)
                      .map((c) => c.email)
                      .filter(Boolean)
                      .join(","),
                  })
                }
                disabled={!emailContact}
                className="inline-flex items-center gap-1.5 border border-slate-300 disabled:opacity-50 text-slate-700 px-3 py-1.5 rounded-md text-[12.5px]"
              >
                <Mail size={13} /> Email draft
              </button>
              {resolvable && (
                <>
                  <button
                    onClick={() =>
                      resolveTouch(row, {
                        state: "done", doneAt: Date.now(), doneBy: user?.id, method: "call",
                      })
                    }
                    className="inline-flex items-center gap-1.5 bg-[#1E293B] hover:bg-slate-700 text-white px-3 py-1.5 rounded-md text-[12.5px] font-medium"
                  >
                    <Check size={13} /> Mark done
                  </button>
                  <button
                    onClick={() =>
                      updateTouch(row.plan.dealId, row.touchKey, {
                        snoozedUntil: moment(today).add(7, "days").format("YYYY-MM-DD"),
                      })
                        .then(() => setOpenRow(null))
                        .catch(() => toast.error(UPDATE_FAILED))
                    }
                    className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-md text-[12.5px]"
                  >
                    Snooze 1 week
                  </button>
                  <button
                    onClick={() =>
                      resolveTouch(row, { state: "skipped", doneAt: Date.now(), doneBy: user?.id })
                    }
                    className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded-md text-[12.5px]"
                  >
                    Skip this one
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-[96%] mx-auto my-8">
      <PageHeader
        title="Past client follow-ups"
        subtitle="Stay in touch after closing: monthly at first, then every home anniversary."
      />
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {TABS.filter((t) => t !== "Team View" || isAdmin).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-medium ${
              tab === t
                ? "text-slate-900 border-b-2 border-[#218FDA]"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t}
            {t === "Due" && dueCount > 0 ? ` (${dueCount})` : ""}
          </button>
        ))}
      </div>

      {tab === "Team View" ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-slate-500">
              Done and Skipped reflect the period below. Due and Overdue are always current.
            </div>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1"
            >
              {PERIODS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Overdue</th>
                  <th className="px-4 py-3">Done</th>
                  <th className="px-4 py-3">Skipped</th>
                </tr>
              </thead>
              <tbody>
                {teamCounts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                      No follow-up plans yet. New closings appear here automatically.
                    </td>
                  </tr>
                ) : (
                  teamCounts.map((r) => (
                    <tr
                      key={r.agentId}
                      onClick={() => setAgentFilter({ id: r.agentId, name: r.name })}
                      className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                      <td className="px-4 py-3">{r.due}</td>
                      <td className="px-4 py-3 text-red-700 font-medium">{r.overdue}</td>
                      <td className="px-4 py-3 text-green-700">{r.done}</td>
                      <td className="px-4 py-3 text-slate-500">{r.skipped}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {agentFilter && (
            <>
              <div className="flex items-center gap-2 mt-5 mb-2">
                <span className="text-sm text-slate-600">
                  Showing: {agentFilter.name}
                </span>
                <button
                  onClick={() => setAgentFilter(null)}
                  className="p-0.5 text-slate-400 hover:text-slate-600"
                  aria-label="Clear agent filter"
                >
                  <X size={14} />
                </button>
                <select
                  value=""
                  onChange={(e) => handleReassign(e.target.value)}
                  aria-label="Reassign all shown to"
                  className="text-sm border border-slate-300 rounded-md px-2 py-1 ml-2"
                >
                  <option value="" disabled>Reassign all shown to…</option>
                  {reassignableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {`${u.firstname || ""} ${u.lastname || ""}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                {agentQueueRows.length === 0 ? (
                  <div className="p-8 text-center text-sm text-slate-500">
                    Nothing due or upcoming for {agentFilter.name}.
                  </div>
                ) : (
                  agentQueueRows.map(renderQueueRow)
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {rows.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              Nothing {tab === "Due" ? "due right now" : tab.toLowerCase()}. New closings join the program automatically.
            </div>
          )}
          {rows.map(renderQueueRow)}
          {tab === "Due" && pausedPlans.length > 0 && (
            <div className="p-4 text-[12.5px] text-slate-500 flex items-center gap-2">
              <PauseCircle size={14} className="text-slate-400" />
              Paused (mid-transaction): {pausedPlans.map((p) => p.clientNames).join(", ")}
            </div>
          )}
        </div>
      )}

      {textTarget && (
        <TextThreadModal
          key={textTarget.row.plan.dealId + textTarget.row.touchKey}
          dealId={textTarget.row.plan.dealId}
          contactId={textTarget.contact?.id}
          contactName={textTarget.row.plan.clientNames}
          initialBody={textTarget.draft.body}
          onClose={() => {
            const t = textTarget;
            setTextTarget(null);
            updateTouch(t.row.plan.dealId, t.row.touchKey, { lastOpenedAt: Date.now() }).catch(
              (e) => console.error("Follow-up touch stamp failed:", e),
            );
          }}
        />
      )}
      {emailTarget && (
        <FollowUpEmailModal
          sender={user}
          toEmail={emailTarget.contact?.email}
          bcc={emailTarget.bcc}
          toName={emailTarget.row.plan.clientNames}
          initialSubject={emailTarget.draft.subject}
          initialBody={emailTarget.draft.body}
          touchKey={emailTarget.row.touchKey}
          dealId={emailTarget.row.plan.dealId}
          propertyAddress={emailTarget.row.plan.propertyAddress}
          onClose={() => setEmailTarget(null)}
          onSent={() => {
            const t = emailTarget;
            setEmailTarget(null);
            resolveTouch(
              t.row,
              { state: "done", doneAt: Date.now(), doneBy: user?.id, method: "email" },
              "Email sent, but marking the check-in done failed. Mark it done manually so the client is not double-messaged.",
            );
          }}
        />
      )}
    </div>
  );
}
